import { useRef } from 'react';
import type { ApiResponse } from '@ihs-widget/api/interfaces';

// The default maximum number of consecutive invalid API responses allowed before accepting data as-is.
// This is to prevent the component from getting stuck when the API is down or returning invalid data  for a shor time. 
const DEFAULT_MAX_ATTEMPTS = 20; // 5 seconds * 20 = 100 seconds


/**
 * Returns display data: if the API response is invalid, returns the last valid response.
 * After maxAttempts consecutive invalid responses (500, null, [], {}), gives up and returns the broken data.
 * Use with validators from validateApiResponse.ts.
 */
export function useValidatedApiData<T>(
    apiResponse: ApiResponse<T> | undefined,
    isValid: (r: ApiResponse<unknown> | undefined) => boolean,
    maxAttempts: number = DEFAULT_MAX_ATTEMPTS
): ApiResponse<T> | undefined {
    const lastValidRef = useRef<ApiResponse<T> | undefined>(undefined);
    const invalidCountRef = useRef(0);
    const prevResponseRef = useRef<ApiResponse<T> | undefined>(undefined);

    if (!apiResponse) return lastValidRef.current;

    if (isValid(apiResponse)) {
        lastValidRef.current = apiResponse;
        invalidCountRef.current = 0;
        prevResponseRef.current = apiResponse;
        return apiResponse;
    }

    if (prevResponseRef.current !== apiResponse) {
        invalidCountRef.current += 1;
        prevResponseRef.current = apiResponse;
    }

    if (invalidCountRef.current >= maxAttempts) {
        return apiResponse;
    }

    return lastValidRef.current;
}
